import { useSendNotification } from "@app/hooks/useNotification";
import { useSpaces } from "@app/lib/swr/spaces";
import type { EnrichedSpaceType, PodType } from "@app/types/space";
import type { LightWorkspaceType } from "@app/types/user";
import { useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import { createContext, useContext, useEffect, useMemo } from "react";

interface SpacesContextType {
  owner: LightWorkspaceType;
  spaces: (EnrichedSpaceType | PodType)[];
  isSpacesLoading: boolean;
  isSpacesError: boolean;
}

const SpacesContext = createContext<SpacesContextType | undefined>(undefined);

export const useSpacesContext = () => {
  const context = useContext(SpacesContext);
  if (!context) {
    throw new Error("useSpacesContext must be used within a SpacesProvider");
  }
  return context;
};

interface SpacesProviderProps {
  owner: LightWorkspaceType;
  children: ReactNode;
}

export const SpacesProvider = ({ owner, children }: SpacesProviderProps) => {
  const { t } = useLingui();
  const sendNotification = useSendNotification();
  const { spaces, isSpacesLoading, isSpacesError } = useSpaces({
    kinds: "all",
    workspaceId: owner.sId,
  });

  useEffect(() => {
    if (isSpacesError) {
      sendNotification({
        type: "error",
        title: t`Failed to load zones`,
        description: t`Unable to fetch workspace zones. Please try again.`,
      });
    }
  }, [isSpacesError, sendNotification, t]);

  const value: SpacesContextType = useMemo(
    () => ({
      owner,
      spaces,
      isSpacesLoading,
      isSpacesError,
    }),
    [owner, spaces, isSpacesLoading, isSpacesError]
  );

  return (
    <SpacesContext.Provider value={value}>{children}</SpacesContext.Provider>
  );
};

SpacesProvider.displayName = "SpacesProvider";
